import type { RefObject } from 'react';
import type { DialogProps } from '@/components/dialog/dialog.types';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import clsx from 'clsx';
import { useOnClickOutside, useScrollLock } from 'usehooks-ts';
import { AnimatePresence, motion } from 'motion/react';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { ID } from '@/utils/enums';
import '@/components/dialog/dialog-v1.css';
import '@/components/dialog/dialog-v2.css';

/**
 * TODO: Improvement for later:
 * 1 - add text fade in and when scrolling and remove this effect when bottom reached.
 * 2 - add bottom panel conditional, that will take array of text and action records for buttons
 * 3 - add other positions on all 4 corners
 * 4 - add support of @media to dialogs sizes
 */

export default function Dialog(props: DialogProps) {
  const {
    children,
    on,
    onClose,
    position = 'top-center',
    size = '1/2',
    maxWidth,
    minWidth,
    maxHeight,
    minHeight,
    heightMax = '100',
    withoutCloseButton,
    doNotCloseOnOutsideClick,
  } = props;
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const ref = useRef<HTMLDivElement>(null);
  const { lock, unlock } = useScrollLock({ lockTarget: 'body', autoLock: false });
  const root = document.querySelector('#root');

  useOnClickOutside(ref as RefObject<HTMLDivElement>, () => {
    if (!doNotCloseOnOutsideClick) onClose?.call(null);
  });

  useEffect(() => {
    on ? lock() : unlock();
    return () => unlock();
  }, [on, lock, unlock]);

  if (!root) return null;

  return createPortal(
    <AnimatePresence>
      {on && (
        <motion.div
          data-css-version={cssVersion}
          className={clsx('backdrop', {
            'backdrop--top-center': position === 'top-center',
            'backdrop--top-left': position === 'top-left',
            'backdrop--top-right': position === 'top-right',
            'backdrop--bottom-center': position === 'bottom-center',
            'backdrop--bottom-left': position === 'bottom-left',
            'backdrop--bottom-right': position === 'bottom-right',
            'backdrop--center': position === 'center',
            'backdrop--one-fifth': size === '1/5',
            'backdrop--one-fourth': size === '1/4',
            'backdrop--one-third': size === '1/3',
            'backdrop--half': size === '1/2',
            'backdrop--two-third': size === '2/3',
            'backdrop--full': size === '1/1',
            'backdrop--content': size === 'content',
          })}
          initial={{ opacity: 0 }}
          animate={
            cssVersion === 1
              ? { opacity: 1, transition: { delay: 0, type: 'spring', stiffness: 300, damping: 30 } }
              : { opacity: 1, transition: { delay: 0, type: 'spring', stiffness: 100, damping: 16 } }
          }
          exit={
            cssVersion === 1
              ? { opacity: 0, transition: { delay: 0.2, type: 'spring', stiffness: 300, damping: 30 } }
              : { opacity: 0, transition: { delay: 0.4, type: 'spring', stiffness: 100, damping: 16 } }
          }
        >
          <motion.div
            ref={ref}
            tabIndex={0}
            className='dialog'
            style={{
              maxWidth,
              minWidth,
            }}
            initial={cssVersion === 1 ? { y: -40, scale: 0.9, opacity: 0 } : { y: '100vh', scale: 0.9, opacity: 0.75 }}
            animate={
              cssVersion === 1
                ? {
                    y: 0,
                    scale: 1,
                    opacity: 1,
                    transition: { delay: 0.2, type: 'spring', stiffness: 300, damping: 30 },
                  }
                : {
                    y: 0,
                    scale: 1,
                    opacity: 1,
                    transition: { delay: 0.4, type: 'spring', stiffness: 100, damping: 16 },
                  }
            }
            exit={
              cssVersion === 1
                ? {
                    y: -40,
                    scale: 0.9,
                    opacity: 0,
                    transition: { delay: 0, type: 'spring', stiffness: 300, damping: 30 },
                  }
                : {
                    y: '100vh',
                    scale: 0.9,
                    opacity: 0.75,
                    transition: { delay: 0, type: 'spring', stiffness: 100, damping: 16 },
                  }
            }
          >
            {!withoutCloseButton && (
              <motion.button
                type='button'
                aria-label='Close'
                className='dialog__close-button'
                whileTap={{ scale: 1.125 }}
                onClick={() => setTimeout(() => onClose?.call(null), 125)}
              >
                <X />
              </motion.button>
            )}
            <div id={ID.DIALOG_CONTENT} className={clsx('dialog__content', `dialog__content--${heightMax}`)}>
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    root,
  );
}
